import moment from 'moment';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../common/components/Grid/Grid';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import {
  PromoterModel,
  LinkTypes,
  PromoterTypes,
  sortPromotersByOperatorAndDateCreated,
} from '@mm/state/model/PromoterModel';
import { ThreeDotMenu } from '../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { usePromoterJSCode } from '../../use-promoter-js-code';
import { useStartBotDetails } from '../../use-startbot-details';
import './StartBotPromoterTable.pcss';

type Props = {
  onChangePage: (page: number) => any;
  promoters: PromoterModel[];
  kind: LinkTypes;
  singularName: string;
  createStartBot?: () => void;
};

const shouldShowLinkCode = (kind: LinkTypes) =>
  [PromoterTypes.CUSTOMER_CHAT, PromoterTypes.OPTIN].includes(kind);
const shouldShowPostId = (kind: LinkTypes) =>
  kind === PromoterTypes.FB_COMMENT_GUARD;
const showTableHeader = (kind: LinkTypes) =>
  kind === PromoterTypes.FB_COMMENT_GUARD;

const actions = [
  { name: 'Edit', value: 'edit' },
  { name: 'Delete', value: 'delete' },
];

const StartBotPromoterTableRow = ({
  promoter,
  kind,
}: {
  promoter: PromoterModel;
  kind: LinkTypes;
}) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const {
    history,
    match: {
      params: { chatbotId },
    },
  } = useRouter<{ chatbotId: string }>();
  const promoterCRUDActions = usePromoterCRUDActions();
  const dialogues = useSelector((state: StoreState) => state.page.entities);
  const startBotDialogue = dialogues.find(
    dialogue => dialogue.id === promoter.pageId,
  );
  const promoterJSCode = usePromoterJSCode(promoter, startBotDialogue);
  const startBotDetails = useStartBotDetails(kind);

  const onClickDelete = (promoter: PromoterModel) => {
    const deleteCallback = () => {
      promoterCRUDActions.delete(promoter);
    };

    dispatch(
      modalActions.openDeleteConfirmModal({
        headerText: `Are you sure you want to delete this ${startBotDetails.singularPromoterName}?`,
        deleteCallback,
      }),
    );
  };

  const onClickEdit = (promoter: PromoterModel) => {
    if (promoter.id) {
      history.push(
        startBotDetails.routes.edit.getLink({
          chatbotId: Number(chatbotId),
          dialogueId: promoter.pageId,
          promoterId: promoter.id,
        }),
      );
    }
  };

  const onSelectItem = (value: string) => {
    if (value === 'edit') {
      onClickEdit(promoter);
    }
    if (value === 'delete') {
      onClickDelete(promoter);
    }
  };

  if (promoter) {
    const formattedUpdatedAt = moment(promoter.updatedAt).format('MM/DD/YYYY');
    const formattedCreatedAt = moment(promoter.createdAt).format('MM/DD/YYYY');

    return (
      <GridRow
        key={promoter.id}
        className="start-bot-promoter-table__table-row"
      >
        <GridCell
          onClick={() => onClickEdit(promoter)}
          fr={2.5}
          className="start-bot-promoter-table__table-name"
        >
          <strong>{promoter.name}</strong>
        </GridCell>

        <GridCell>{promoter.active ? 'Active' : 'Inactive'}</GridCell>

        {shouldShowLinkCode(kind) && (
          <GridCell>
            <CopyToClipboard text={promoterJSCode}>
              <span className="start-bot-promoter-table__copy-link">
                Copy Code
              </span>
            </CopyToClipboard>
          </GridCell>
        )}

        {shouldShowPostId(kind) && <GridCell>{promoter.postId}</GridCell>}

        <GridCell>{formattedUpdatedAt}</GridCell>
        <GridCell>{formattedCreatedAt}</GridCell>

        <GridCell
          overflowable
          className="start-bot-promoter-table--action-cell"
        >
          <ThreeDotMenu elements={actions} selectItem={onSelectItem} />
        </GridCell>
      </GridRow>
    );
  }

  return null;
};

export const StartBotPromoterTable = (props: Props) => {
  const { promoters, kind, createStartBot, singularName } = props;

  const sortedPromoters = useMemo(
    () => sortPromotersByOperatorAndDateCreated(promoters),
    [promoters],
  );

  return (
    <div className="start-bot-promoter-table">
      {showTableHeader(kind) && createStartBot && (
        <div className="start-bot-promoter-table__header">
          <span className="start-bot-promoter-table__header--title">
            {pluralize(singularName)}
          </span>
          <PrimaryButton
            onClick={() => createStartBot()}
            className="start-bot-promoter-table__header--action"
          >
            + Create New
          </PrimaryButton>
        </div>
      )}

      <Grid className="start-bot-promoter-table__table">
        <GridLabels>
          <GridCell fr={2.5}>Name</GridCell>
          <GridCell>Status</GridCell>

          {shouldShowLinkCode(kind) && <GridCell>Link/Code</GridCell>}

          {shouldShowPostId(kind) && <GridCell>Post ID</GridCell>}
          <GridCell>Updated</GridCell>
          <GridCell>Created</GridCell>
          <GridCell className="start-bot-promoter-table--action-cell">
            Actions
          </GridCell>
        </GridLabels>

        {sortedPromoters.map(promoter => (
          <StartBotPromoterTableRow
            key={promoter.id}
            promoter={promoter}
            kind={kind}
          />
        ))}
      </Grid>
    </div>
  );
};
